import { createEventDispatcher } from "svelte";
import type { CamelCase, KebabCase, SnakeCase } from "type-fest";
import { get, type Readable } from "svelte/store";
import { sheet } from "../index";
import type { Events } from "../SheetV1.types";
import type { LoadError } from "./externalContent";

/**
 *
 *
 */
type ExtractEvent<T extends string> = T extends `oc-${infer U}` ? CamelCase<U> : never;

/**
 *
 */
type EventNames = ExtractEvent<keyof Events>;

/**
 *
 */
type ToOcEventName<T extends EventNames> = `oc-${KebabCase<T>}` extends keyof Events
  ? `oc-${KebabCase<T>}`
  : never;

/**
 *
 */
export type ExtractDetail<T extends CustomEvent> = T extends CustomEvent<infer U> ? U : never;

/**
 *
 */
export type GetSheetDetailByName<T extends keyof Events> = ExtractDetail<Events[T]>;

const EVENTS: { [P in keyof Events as ExtractEvent<P>]: P } = {
  open: "oc-open",
  close: "oc-close",
  switch: "oc-switch",
  contentLoaded: "oc-content-loaded",
  contentLoadingError: "oc-content-loading-error",
} as const;

export const CLOSE_TYPES: { [P in CloseTypes as Uppercase<SnakeCase<P & string>>]: P } = {
  CLOSED_WITH_X: "closedWithX",
  CLOSED_WITH_CURTAIN_CLICK: "closedWithCurtainClick",
  CLOSED_WITH_DRAG: "closedWithDrag",
  CLOSED_WITH_ESCAPE: "closedWithEscape",
  CLOSED_PROGRAMMATICALLY: "closedProgrammatically",
};

export type CloseTypes =
  | "closedWithX"
  | "closedWithCurtainClick"
  | "closedWithDrag"
  | "closedWithEscape"
  | "closedProgrammatically";

function emitEventFunc<T extends EventNames>(
  host: HTMLOcSheetV1Element,
  openSheet: Readable<HTMLOcSheetV1Element | null>,
  dispatch: ReturnType<typeof createEventDispatcher<Events>>,
  name: T,
  payload: Omit<ExtractDetail<Events[ToOcEventName<T>]>, "instance">,
): void {
  /*                                        */
  /*                                                                     */
  dispatch(EVENTS[name], { ...payload, instance: host });

  /*                                                         */
  /*                                           */
  const openInstance = get(openSheet);
  if (openInstance && openInstance !== host) return;

  /*                                                                            */
  /*                                                                                     */
  sheet[`${name}Event`].emit({ ...payload, instance: openInstance ?? host });
}

/**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
export function useSheetEvents(
  host: HTMLOcSheetV1Element,
  openSheet: Readable<HTMLOcSheetV1Element | null>,
  sheetElementVisible: Readable<boolean>,
  isSwitching: Readable<boolean>,
  requestedClose: Readable<CloseTypes | null>,
  externalContentApplied: Readable<boolean>,
  externalContentError: Readable<Error | LoadError | null>,
): void {
  const dispatch = createEventDispatcher<Events>();
  const emitEvent = emitEventFunc.bind(null, host, openSheet, dispatch);

  /*                                                    */
  /*                                                                                */
  sheetElementVisible.subscribe((visible) => {
    if (visible) emitEvent(get(isSwitching) ? "switch" : "open", {});
  });

  /*                                                              */
  requestedClose.subscribe((closeType) => {
    if (closeType) emitEvent("close", { closeType });
  });

  /*                                                                       */
  /*               */
  externalContentApplied.subscribe((applied) => {
    if (applied) emitEvent("contentLoaded", {});
  });

  /*                                                          */
  /*                                                                      */
  externalContentError.subscribe((error) => {
    if (error) emitEvent("contentLoadingError", { error });
  });
}
